import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, TextField, ThemeProvider, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import KeyIcon from '@mui/icons-material/Key';
import SearchIcon from '@mui/icons-material/Search';
import iziToast from 'izitoast';

import { Header } from '../../components/common';
import ChangePasswordModal from '../../components/Modals/ChangePasswordModal';
import {
  activateUser, createUser, deactivateUser, fetchUsers, updateOwnProfile, updateUser,
} from '../../actions/user';
import { internalServerError } from '../../utils/consts/pathRoutes';
import { journalTheme } from '../../utils/consts/journalTheme';
import { resetCurrentUser, useCurrentUser } from '../../utils/currentUser';
import UserDialog, { ROLES } from './UserDialog';
import '../../components/Journal/journal.css';

const ROLE_COLOR = {
  'Администратор': 'error',
  'Редактор': 'warning',
  'Преподаватель ЭЖ': 'info',
};

const notifyError = (e) => iziToast.error({ message: internalServerError(e), position: 'topRight' });

/**
 * Личный кабинет: свой профиль и, для администратора, таблица сотрудников.
 *
 * Раньше здесь был список задач, а сотрудники выводились списком карточек, по клику на
 * которые данные подставлялись в форму своего профиля — легко было перепутать, чьи данные
 * правишь. Теперь профиль и сотрудники разведены: у каждого сотрудника своя строка и
 * своё окно правки.
 */
const Account = () => {
  const { user: me, loading: meLoading } = useCurrentUser();
  const isAdmin = me?.role === 'Администратор';

  // ─── Свой профиль ─────────────────────────────────────────────────────────
  const [profile, setProfile] = useState({ name: '', email: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  useEffect(() => {
    if (me) setProfile({ name: me.name || '', email: me.email || '' });
  }, [me]);

  const profileChanged = me && (profile.name !== me.name || profile.email !== me.email);

  const saveProfile = () => {
    setProfileSaving(true);
    updateOwnProfile({ name: profile.name.trim(), email: profile.email.trim() })
      .then((data) => {
        resetCurrentUser();
        iziToast.success({ message: data.message, position: 'topRight' });
      })
      .catch(notifyError)
      .finally(() => setProfileSaving(false));
  };

  // ─── Сотрудники ───────────────────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [sort, setSort] = useState({ field: 'name', direction: 'asc' });
  const [dialog, setDialog] = useState(null);     // { user } — правка, {} — создание
  const [confirm, setConfirm] = useState(null);   // { user, action: 'deactivate' | 'activate' }

  const loadUsers = () => {
    setUsersLoading(true);
    fetchUsers()
      .then(setUsers)
      .catch(notifyError)
      .finally(() => setUsersLoading(false));
  };

  useEffect(() => {
    if (isAdmin) loadUsers();
  }, [isAdmin]);

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();

    return users
      .filter((user) => !roleFilter || user.role === roleFilter)
      .filter((user) => statusFilter === 'all'
        || (statusFilter === 'active' ? user.is_active !== 0 : user.is_active === 0))
      .filter((user) => !query
        || (user.name || '').toLowerCase().includes(query)
        || (user.email || '').toLowerCase().includes(query))
      .sort((a, b) => {
        const left = String(a[sort.field] || '').toLowerCase();
        const right = String(b[sort.field] || '').toLowerCase();
        const order = left.localeCompare(right, 'ru');
        return sort.direction === 'asc' ? order : -order;
      });
  }, [users, search, roleFilter, statusFilter, sort]);

  const counts = useMemo(() => ({
    active: users.filter((user) => user.is_active !== 0).length,
    inactive: users.filter((user) => user.is_active === 0).length,
  }), [users]);

  const toggleSort = (field) => setSort((prev) => ({
    field,
    direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
  }));

  const submitUser = (payload) => {
    const isEdit = Boolean(dialog?.user);
    const request = isEdit ? updateUser(dialog.user.id, payload) : createUser(payload);

    return request
      .then((data) => {
        iziToast.success({ message: data.message, position: 'topRight' });
        setDialog(null);
        loadUsers();
      })
      .catch(notifyError);
  };

  const runConfirm = () => {
    const { user, action } = confirm;
    const request = action === 'deactivate' ? deactivateUser(user.id) : activateUser(user.id);

    request
      .then((data) => {
        iziToast.success({ message: data.message, position: 'topRight' });
        loadUsers();
      })
      .catch(notifyError)
      .finally(() => setConfirm(null));
  };

  const sortable = (field, label) => (
    <TableSortLabel
      active={sort.field === field}
      direction={sort.field === field ? sort.direction : 'asc'}
      onClick={() => toggleSort(field)}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <>
      <Header />

      <ThemeProvider theme={journalTheme}>
        <div className="journal-layout">
          {/* ─── Мой профиль ─── */}
          <Paper sx={{ p: 3, mb: 4, maxWidth: 720 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <h2 className="journal-title">Мой профиль</h2>
              {me && <Chip label={me.role} color={ROLE_COLOR[me.role] || 'default'} variant="outlined" />}
            </Stack>

            {meLoading && !me ? (
              <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress color="warning" size={24} /></Stack>
            ) : (
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
                  <TextField
                    label="ФИО" value={profile.name} sx={{ flex: 1, minWidth: 260 }}
                    onChange={(event) => setProfile((prev) => ({ ...prev, name: event.target.value }))}
                  />
                  <TextField
                    label="Логин (email)" value={profile.email} sx={{ flex: 1, minWidth: 260 }}
                    onChange={(event) => setProfile((prev) => ({ ...prev, email: event.target.value }))}
                  />
                </Stack>

                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <Button startIcon={<KeyIcon />} onClick={() => setPasswordOpen(true)}>
                    Сменить пароль
                  </Button>
                  <Button
                    variant="contained" onClick={saveProfile}
                    disabled={!profileChanged || profileSaving || !profile.name.trim() || !profile.email.trim()}
                  >
                    Сохранить
                  </Button>
                </Stack>
              </Stack>
            )}
          </Paper>

          {/* ─── Сотрудники (только администратор) ─── */}
          {isAdmin && (
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="baseline" spacing={2}>
                  <h2 className="journal-title">Сотрудники</h2>
                  <span className="journal-muted">
                    активных {counts.active}{counts.inactive > 0 ? ` · отключённых ${counts.inactive}` : ''}
                  </span>
                </Stack>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({})}>
                  Добавить сотрудника
                </Button>
              </Stack>

              <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <TextField
                  label="Поиск по ФИО или логину" value={search} sx={{ minWidth: 300 }}
                  onChange={(event) => setSearch(event.target.value)}
                  InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: '#8a8a8a' }} fontSize="small" /> }}
                />
                <TextField
                  select label="Роль" value={roleFilter} sx={{ width: 220 }}
                  onChange={(event) => setRoleFilter(event.target.value)}
                >
                  <MenuItem value="">Все роли</MenuItem>
                  {ROLES.map((role) => <MenuItem key={role.value} value={role.value}>{role.value}</MenuItem>)}
                </TextField>
                <TextField
                  select label="Статус" value={statusFilter} sx={{ width: 180 }}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <MenuItem value="active">Активные</MenuItem>
                  <MenuItem value="inactive">Отключённые</MenuItem>
                  <MenuItem value="all">Все</MenuItem>
                </TextField>
              </Stack>

              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{sortable('name', 'ФИО')}</TableCell>
                      <TableCell>{sortable('email', 'Логин')}</TableCell>
                      <TableCell>{sortable('role', 'Роль')}</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell align="right">Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usersLoading && users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <CircularProgress color="warning" size={24} />
                        </TableCell>
                      </TableRow>
                    )}

                    {!usersLoading && visible.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }} className="journal-muted">
                          Сотрудники не найдены
                        </TableCell>
                      </TableRow>
                    )}

                    {visible.map((user) => {
                      const isMe = user.id === me?.id;
                      const isInactive = user.is_active === 0;

                      return (
                        <TableRow key={user.id} hover sx={{ opacity: isInactive ? 0.55 : 1 }}>
                          <TableCell>
                            <b>{user.name}</b>
                            {isMe && <span className="journal-muted"> · это вы</span>}
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Chip
                              size="small" label={user.role}
                              color={ROLE_COLOR[user.role] || 'default'} variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {isInactive
                              ? <Chip size="small" label="отключён" />
                              : <span className="journal-muted">активен</span>}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Изменить">
                              <IconButton size="small" onClick={() => setDialog({ user })}>
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            {isInactive ? (
                              <Tooltip title="Вернуть доступ">
                                <IconButton size="small" onClick={() => setConfirm({ user, action: 'activate' })}>
                                  <RestoreIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title={isMe ? 'Нельзя отключить себя' : 'Отключить доступ'}>
                                <span>
                                  <IconButton
                                    size="small" color="error" disabled={isMe}
                                    onClick={() => setConfirm({ user, action: 'deactivate' })}
                                  >
                                    <BlockIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </div>

        <UserDialog
          open={Boolean(dialog)} user={dialog?.user}
          onClose={() => setDialog(null)} onSubmit={submitUser}
        />

        <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)}>
          <DialogTitle>
            {confirm?.action === 'deactivate' ? 'Отключить доступ' : 'Вернуть доступ'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {confirm?.action === 'deactivate'
                ? `${confirm?.user.name} больше не сможет войти, все его сеансы завершатся. `
                  + 'Данные и авторство записей сохранятся — доступ можно вернуть в любой момент.'
                : `${confirm?.user.name} снова сможет войти в систему.`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirm(null)}>Отмена</Button>
            <Button
              variant="contained" color={confirm?.action === 'deactivate' ? 'error' : 'primary'}
              onClick={runConfirm}
            >
              {confirm?.action === 'deactivate' ? 'Отключить' : 'Вернуть'}
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      <ChangePasswordModal open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </>
  );
};

export default Account;
